import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { ISelectFieldSingleItem } from "../../components/Form/DynamicForm";
import { IAxiosDefaultDataObject } from "../../config";
import BaseApp, { IBaseApp, IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional, ISearchParam } from "../BaseApp";
import { addNewUser, deleteUser, setSelectedUsers, updateAllUsers, updateSingleUser, updateUsersLoadingStatus, updateUsersPagination, updateUsersSearch, updateUsersSearchParams, useReturnAllUsers, useReturnSelectedUsers, useReturnUsersLoadingStatus, useReturnUsersPagination, useReturnUsersSearch, useReturnUsersSearchParams } from "./userSlice";
import { IUserModel, UserTheme } from "../../services/user";
import { IAdminDynamicBasePageProps } from "../admin/AdminDynamicBasePage";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import { ICellRendererParams } from "ag-grid-community";
import { Link } from "react-router-dom";
import UserGroupsApp from "../user_groups/UserGroupsApp";
import { JSX } from "react/jsx-runtime";
import ViewSingleUserPage from "./ViewSingleUserPage";
import api from "../../services/api";

export interface IStrongPasswordResponse {
    lowercase: boolean,
    uppercase: boolean,
    numbers: boolean,
    special_character: boolean,
    length: boolean,
    valid: boolean
}

export const verifyStrongPassword = (password: string) => {

    const chars = Array.from(password)

    let response: IStrongPasswordResponse = {
        lowercase: false,
        uppercase: false,
        numbers: false,
        special_character: false,
        length: false,
        valid: false
    }

    if (password.length < 8){
        response.length = false
    }else{
        response.length = true
    }

    if (chars.some(char => /[A-Z]/.test(char))){
        response.uppercase = true
    }else{
        response.uppercase = false
    }

    if (chars.some(char => /[a-z]/.test(char))){
        response.lowercase = true
    }else{
        response.lowercase = false
    }

    if (chars.some(char => !isNaN((char as any)) && char !== ' ')){
        response.numbers = true
    }else{
        response.numbers = false
    }

    if (chars.some(char => /[!@#$%^&*(),.?":{}|<>-]/.test(char))){
        response.special_character = true
    }else{
        response.special_character = false
    }

    if (response.lowercase && response.uppercase && response.numbers && response.special_character && response.length){
        response.valid = true
    }else{
        response.valid = false
    }

    return response

}

/**
 * Users App
 */
export default class UsersApp extends BaseApp implements IBaseApp {

    appName = "users"
    appBaseEndPoint = "/users/accounts"

    customListEndPoint: string = this.appBaseEndPoint

    elemPrefix: string = "users_"

    /**
     * User types
     */
    userTypes: ISelectFieldSingleItem[] = [
        {
            name: "Staff",
            value: "staff"
        },
        // {
        //     name: "Admin",
        //     value: "admin"
        // },
        {
            name: "Admin",
            value: "superuser"
        },
        {
            name: "Guest",
            value: "guest"
        }
    ]

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "thumbnail",
                type: "thumbnail",
                label: "Avatar",
                maxWidth: 150,
                minWidth: 150,
            },
            {
                id: "title",
                type: "text",
                label: "Full name",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "status",
                type: "status",
                label: "Status",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "type",
                type: "status",
                label: "Type",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "email",
                type: "text",
                label: "Email",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "date_joined",
                type: "date",
                label: "Date Created",
                maxWidth: 250,
                minWidth: 250,
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No users have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "users_form",
            elements: [
                {
                    id: this.elemPrefix + "first_name",
                    xs: 4,
                    type: "TextField",
                    label: "First name",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "last_name",
                    xs: 4,
                    type: "TextField",
                    label: "Last name",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "type",
                    xs: 4,
                    type: "Select",
                    label: "Type",
                    fullWidth: true,
                    items: this.userTypes
                },
                {
                    id: this.elemPrefix + "email",
                    xs: 6,
                    type: "TextField",
                    label: "Email",
                    fullWidth: true
                },
                 {
                    id: this.elemPrefix + "group",
                    xs: 6,
                    type: "DynamicModelSelect",
                    label: "Department",
                    fullWidth: true,
                    appClass: new UserGroupsApp(),
                    selectLabel: "Select Department",
                    gridColumns: new UserGroupsApp().gridDataColumns
                },
                {
                    id: this.elemPrefix + "silent_create",
                    xs: 12,
                    type: "Checkbox",
                    label: "Do not notify user that the account is created.",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the user details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "first_name",
                message: "First name is required!"
            },
            {
                id: this.elemPrefix + "last_name",
                message: "Last name is required!"
            },
            {
                id: this.elemPrefix + "type",
                message: "Type is required!"
            },
            {
                id: this.elemPrefix + "email",
                message: "Email is required!"
            },
        ]
    }

    quickPreview = (id: number) => <ViewSingleUserPage customId={id} />

    /**
     * Redux
     */
    redux = {
        updateItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(updateSingleUser((item as IUserModel))),
        addNewItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(addNewUser((item as IUserModel)))
    }

    /**
     * Dynamic Base Page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllUsers,
        pagination: useReturnUsersPagination,
        loading: useReturnUsersLoadingStatus,
        search: useReturnUsersSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateUsersLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteUser({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewUser((item as IUserModel))),
        updateItem: (item, dispatch) => dispatch(updateSingleUser((item as IUserModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllUsers((items as IUserModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateUsersSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateUsersPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/users/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/users/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
        // filterForm: [],
        // customHeaderOptionActionButtons: [],
        // viewItemTemplateLink: "",
        // disableAutoViewItemLink: false,
        // hidePageHeadSection: false,
        // customTitlePageHeadSection: "",
        // disableSearch: false,
        // customPageHeadSectionActionButtons: []
    }

    /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        // {
        //     field: "thumbnail",
        //     headerName: "Avatar",
        //     type: "thumbnail",
        //     resizable: false,
        //     width: 110,
        // },
        // {
        //     field: "full_name",
        //     headerName: "Full Name",
        //     width: 250,
        //     cellRenderer: (params: ICellRendererParams<IUserModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/users/view/${item.id}`}>
        //                     <h3 className="title">{item.full_name}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
        {
            field: "full_name",
            headerName: "Full name",
            type: "title",
            width: 350,
            minWidth: 350,
            cellRendererParams: {
                appClass: () => new UsersApp(),
                enableQuickPreview: true,
            }
        },
        {
            field: "group_title",
            headerName: "Department",
        },
        {
            field: "status",
            headerName: "Status",
            type: "status"
        },
        {
            field: "type",
            headerName: "Type",
            type: "status"
        },
        {
            field: "email",
            headerName: "Email",
            width: 250,
        },
        {
            field: "date_joined",
            headerName: "Date Joined",
            type: "date",
            width: 250,
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/users/view/[id]",
        editItemLink: "/panel/users/edit/[id]",
        filterForm: {
            name: "users-data-displayer-filter-form",
            elements: [
                {
                    id: "group_id",
                    xs: 12,
                    type: "DynamicModelSelect",
                    label: "Select Department",
                    fullWidth: true,
                    appClass: new UserGroupsApp(),
                    selectLabel: "Select Department",
                    gridColumns: new UserGroupsApp().gridDataColumns
                },
            ]
        }
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedUsers,
        searchParams: useReturnUsersSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedUsers(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updateUsersSearchParams(params))
    }
    
    /**
     * Update theme
     */
    updateTheme = async(
        theme: UserTheme
    ) => {
        const response = await api.post("/users/account/update-theme", {
            theme
        })
        return response
    }

}